import { createPortal } from 'react-dom';

import JxForm from '../../../JxControls/JxForms/JxForm';
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar';
import JxButton from '../../../JxControls/JxButtons/JxButton';
import '../../../JxControls/JxTheme.css';
import './QuestionDlg.css';

/**
 * QuestionDlg — generic confirm dialog. Derived from JxForm 20260704 so it drags, z-indexes
 * and looks like every other Jx dialog (JxTitleBar, JxButton) instead of its own bespoke
 * drag/backdrop/button implementation.
 *
 * Callers keep rendering it permanently mounted with an `open` prop (same as every JxForm
 * dialog) — e.g. GlobalDeleteConfirmDlg toggles `open` via DeleteConfirmEngine state.
 *
 * Props:
 *   open      — boolean
 *   title     — string, shown in titlebar
 *   message   — string or ReactNode, shown in body. Strings support \n (whiteSpace: pre-line);
 *               pass JSX for structured content (e.g. a centered entry title line).
 *   buttons   — [{ label, onClick, color? }] — color (any truthy value) renders that button
 *               with the jx-button-danger variant, otherwise jx-button-secondary.
 *   id        — optional DOM id override — only needed if more than one QuestionDlg could
 *               ever be open/mounted at the same time (defaults to 'QuestionDlg_01').
 */
export default class QuestionDlg extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'QuestionDlg';
        this.schema = {};
    }

    // Literal, hardcoded default — no window.innerWidth/innerHeight centering math.
    // width/height not read by panelStyle (JxForm, non-resizable) — CSS owns the real size,
    // this is only for geo-shape consistency.
    getDefaultGeo() {
        return { top: 460, left: 810, width: 300, height: 160 };
    }

    // No default close behaviour — this dialog only closes via the caller's own
    // Cancel/Delete button onClick handlers (open prop is owned by the caller/engine).
    handleClose = () => {};

    render() {
        if (!this.props.open) return null;

        const { title, message, buttons = [] } = this.props;

        return createPortal(
            <>
                <div className="qdlg-backdrop" style={{ zIndex: this.state.zIndex - 1 }} />

                <div {...this.panelProps} style={this.panelStyle} className={`qdlg-panel ${this.panelActiveClass}`}>
                    <JxTitleBar
                        id={`${this.name}_titlebar`}
                        title={title}
                        variant="toolwindow"
                        isDraggable={true}
                        dragHandleProps={this.dragHandleProps}
                    />

                    <div className="qdlg-body">{message}</div>

                    <div className="qdlg-footer">
                        {buttons.map((btn) => (
                            <JxButton
                                key={btn.label}
                                label={btn.label}
                                onClick={btn.onClick}
                                className={btn.color ? 'jx-button-danger' : 'jx-button-secondary'}
                            />
                        ))}
                    </div>
                </div>
            </>,
            document.body,
        );
    }
}
